// API Configuration
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://crmapi.rewarealtors.com/api' ;
export const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;

// API Endpoints https://crmapi.rewarealtors.com
export const API_ENDPOINTS = {
  // Authentication
  auth: {
    login: '/auth/login',
    logout: '/auth/logout',
    changePassword: '/auth/change-password'
  },
  
  // Sessions & Audit
  sessions: {
    active: '/sessions/active',
    userSessions: (userId) => `/sessions/user/${userId}`,
    terminate: (sessionId) => `/sessions/terminate/${sessionId}`,
    terminateUser: (userId) => `/sessions/terminate-user/${userId}`,
    auditLogs: '/sessions/audit-logs',
    auditRecordHistory: '/sessions/audit-logs/record',
    auditLogsExport: '/sessions/audit-logs/export',
    auditSummary: '/sessions/audit-summary',
    auditEmployeeSummary: '/sessions/audit-employee-summary',
    auditLeaderboard: '/sessions/audit-leaderboard',
    statistics: '/sessions/statistics'
  },
  
  // Users & Employees
  users: {
    profile: '/users/profile',
    employees: '/users/employees',
    allEmployees: '/users/allemployees',
    addEmployee: '/users/add-employee',
    updateEmployee: (id) => `/users/employee/${id}`,
    addProfile: '/users/add-profile',
    employeesProperty: '/users/employeesproperty'
  },
  
  // Leads
  leads: {
    base: '/leads',
    calendar: '/leads/calender',
    add: '/leads/add',
    update: (id) => `/leads/update/${id}`,
    delete: (id) => `/leads/delete/${id}`,
    convertToClient: (id) => `/leads/${id}/convert-to-client`,
    updateStatus: (id) => `/leads/updateStatus/${id}`,
    getById: (id) => `/leads/${id}`,
    assignEmployee: (id) => `/leads/asignleademployee/${id}`,
    getLeadEmployee: '/leads/getleademployeee',
    getAllClosed: '/leads/getAllLeadsClosed',
    markEventAsDone: (oppId, commentId, eventType) => `/leads/markEventAsDone/${oppId}/${commentId}/${eventType}`,
    remindersPreview: '/leads/reminders/preview',
    remindersRun: '/leads/reminders/run',
  },
  
  // Opportunities
  opportunities: {
    base: '/opportunities',
    pipeline: '/opportunities/pipeline',
    visible: '/opportunities/visible',
    pdf: (id) => `/opportunities/pdf/${id}`,
    delete: (id) => `/opportunities/${id}/delete`,
    updateStatus: (id) => `/opportunities/updatestatus/${id}`,
    comment: (id) => `/opportunities/opportunity/${id}/comment`,
    loa: (id) => `/opportunities/${id}/loa`,
    loaAdd: (id) => `/opportunities/${id}/loaadd`,
    agreement: (id) => `/opportunities/${id}/agreement`,
    agreementAdd: (id) => `/opportunities/${id}/agreementadd`,
    siteVisit: (id) => `/opportunities/${id}/site-visit`,
    followUps: (id) => `/opportunities/${id}/follow-ups`,
    verifyProposal: (id) => `/opportunities/verify-proposal/${id}`,
    sendProposalMail: (id) => `/opportunities/send-proposal-mail/${id}`,
    visitors: (id) => `/proposal/${id}/visitors`,
    remindersPreview: '/opportunities/reminders/preview',
    remindersRun: '/opportunities/reminders/run',
  },
  
  // Clients
  clients: {
    base: '/clients',
    byId: (id) => `/clients/client/${id}`,
    assign: (id) => `/clients/${id}/assign`,
    update: (id) => `/clients/${id}`,
    recommend: (id) => `/recommend/recommend/${id}`,
    feFoundProperty: (clientId) => `/clients/${clientId}/fe-found-property`,
    feFoundPropertyEntry: (clientId, propertyId) =>
      `/clients/${clientId}/fe-found-property/${propertyId}`,
  },

  // Properties
  properties: {
    base: '/properties',
    myProperty: '/properties/myproperty',
    archive: '/properties/archive',
    archiveById: (id) => `/properties/${id}/archive`,
    byId: (id) => `/properties/${id}`,
    recommend: (id) => `/recommend/recommend/property/${id}`,
    unarchive: (id) => `/properties/${id}/unarchive`,
    roadmap: (id) => `/properties/${id}/roadmap`,
    termSheet: (id) => `/properties/${id}/term-sheet`,
  },

  // Links & Property Linking
  links: {
    linkProperty: '/link/link-property',
    linkClient: (clientId) => `/link/client/${clientId}`
  },
  
  // Proposals
  proposals: {
    trackVisit: '/proposal/track-visit',
    opportunity: (id) => `/proposal/opportunity/${id}`,
    visitors: (id) => `/proposal/${id}/visitors`
  },
  
  // Reports
  reports: {
    count: '/count',
    myReport: '/reports/myreport',
    availableBrands: '/reports/available-brands',
    tagReports: '/reports/tag-reports',
    leadGeneration: '/reports/lead-generation-report',
    leadToOpportunity: '/reports/lead-to-opportunity-report',
    brandWise: '/reports/brand-wise-report',
    cityWise: (city) => `/reports/city-wise-report/${city}`,
    feProperty: '/reports/fe-property-report',
    summary: '/reports/summary-report',
    tatReport: '/reports/tat-report',
    tatSiteVisitExport: '/reports/tat-site-visit-export',
    tatIndividualReport: '/reports/tat-individual-report',
    adminDashboardFunnel: '/reports/admin-dashboard-funnel',
    employeeKpi: '/reports/employee-kpi',
    employeeKpiMe: '/reports/employee-kpi/me',
    employeeKpiTrends: '/reports/employee-kpi/trends',
    employeeKpiBenchmarks: (employeeId) => `/reports/employee-kpi/${employeeId}/benchmarks`,
    employeeKpiTargetsBulk: '/reports/employee-kpi/targets/bulk',
    employeeKpiTargetsBulkYear: '/reports/employee-kpi/targets/bulk-year',
    employeeKpiYearPlan: '/reports/employee-kpi/year-plan',
    employeeKpiTargets: (employeeId) => `/reports/employee-kpi/${employeeId}/targets`,
    daily: "/reports/daily",
    dailySection: (section) => `/reports/daily/section/${encodeURIComponent(section)}`,
  },

  insights: {
    latest: '/insights/latest',
    history: '/insights/history',
    generate: '/insights/generate',
    runWeekly: '/insights/run-weekly',
    atRisk: '/insights/at-risk',
    settings: '/insights/settings',
  },
  
  // Other
  notifications: {
    base: '/notifications',
    count: '/notifications/count',
    markRead: (id) => `/notifications/${id}/read`,
    markAllRead: '/notifications/read-all',
  },

  followUpTasks: {
    base: '/follow-up-tasks',
    summary: '/follow-up-tasks/summary',
    schedules: '/follow-up-tasks/schedules',
    filterOptions: '/follow-up-tasks/filter-options',
    detail: (id) => `/follow-up-tasks/${id}`,
    complete: (id) => `/follow-up-tasks/${id}/complete`,
    start: (id) => `/follow-up-tasks/${id}/start`,
    reschedule: (id) => `/follow-up-tasks/${id}/reschedule`,
    employeeReports: '/follow-up-tasks/reports/employees',
    sync: '/follow-up-tasks/sync',
    syncStatus: '/follow-up-tasks/sync/status',
  },

  todos: {
    base: '/todos',
    summary: '/todos/summary',
    dashboard: '/todos/dashboard',
    calendar: '/todos/calendar',
    performance: '/todos/performance',
    filterOptions: '/todos/filter-options',
    crmSearch: '/todos/crm-search',
    detail: (id) => `/todos/${id}`,
    start: (id) => `/todos/${id}/start`,
    complete: (id) => `/todos/${id}/complete`,
    reopen: (id) => `/todos/${id}/reopen`,
    cancel: (id) => `/todos/${id}/cancel`,
    reassign: (id) => `/todos/${id}/reassign`,
    comments: (id) => `/todos/${id}/comments`,
    attachments: (id) => `/todos/${id}/attachments`,
  },

  teams: {
    base: '/teams',
    detail: (id) => `/teams/${id}`,
  },

  settings: {
    reminders: '/settings/reminders',
  },

  cities: {
    catalog: '/cities',
    manage: '/cities/manage',
    create: '/cities',
    update: (cityId) => `/cities/${cityId}`,
    addArea: (cityId) => `/cities/${cityId}/areas`,
    updateArea: (cityId, areaId) => `/cities/${cityId}/areas/${areaId}`,
  },
  
  chat: {
    chat: '/chat/chat'
  },
  
  // Data/Analytics
  analytics: {
    opportunityCount: '/opportunities/lol/count/'
  },
  
  track: {
    getAllLeads: '/track/getallLeads',
    track: (id) => `/track/track/${id}`
  },

  hr: {
    dashboard: '/hr/dashboard',
    me: '/hr/me',
    myLearning: '/hr/me/learning',
    employees: '/hr/employees',
    employee: (id) => `/hr/employees/${id}`,
    directoryUsers: '/hr/employees/directory-users',
    archiveEmployee: (id) => `/hr/employees/${id}/archive`,
    activateEmployee: (id) => `/hr/employees/${id}/activate`,
    employeePhoto: (id) => `/hr/employees/${id}/photo`,
    transitionEmployee: (id) => `/hr/employees/${id}/transition`,
    employeeHistory: (id) => `/hr/employees/${id}/history`,
    onboarding: (id) => `/hr/employees/${id}/onboarding`,
    onboardingTask: (id, taskId) => `/hr/employees/${id}/onboarding/${taskId}`,
    documents: (id) => `/hr/employees/${id}/documents`,
    document: (id, documentId) => `/hr/employees/${id}/documents/${documentId}`,
    documentDownload: (id, documentId) => `/hr/employees/${id}/documents/${documentId}/download`,
    documentAck: (id, documentId) => `/hr/employees/${id}/documents/${documentId}/acknowledge`,
    letters: (id) => `/hr/employees/${id}/letters`,
    letterIssue: (id, documentId) => `/hr/employees/${id}/letters/${documentId}/issue`,
    salarySlips: (id) => `/hr/employees/${id}/salary-slips`,
    salarySlipDownload: (id, slipId) => `/hr/employees/${id}/salary-slips/${slipId}/download`,
    leaveBalances: (id) => `/hr/employees/${id}/leave-balances`,
    kpis: (id) => `/hr/employees/${id}/kpis`,
    kpiProgress: (id, kpiId) => `/hr/employees/${id}/kpis/${kpiId}/progress`,
    reviews: (id) => `/hr/employees/${id}/reviews`,
    pips: (id) => `/hr/employees/${id}/pips`,
    confirmations: (id) => `/hr/employees/${id}/confirmations`,
    attempts: (id) => `/hr/employees/${id}/assessments`,
    onboardingTemplates: '/hr/onboarding/templates',
    letterTemplates: '/hr/letters/templates',
    attendance: '/hr/attendance',
    attendanceSummary: '/hr/attendance/summary',
    attendanceExport: '/hr/attendance/export',
    leave: '/hr/leave',
    leaveTypes: '/hr/leave/types',
    leaveDecide: (id) => `/hr/leave/${id}/decide`,
    leaveCancel: (id) => `/hr/leave/${id}/cancel`,
    cycles: '/hr/performance/cycles',
    pipUpdate: (id) => `/hr/performance/pips/${id}`,
    confirmationQueue: '/hr/confirmation',
    confirmationDecide: (id) => `/hr/confirmation/${id}/decide`,
    sessions: '/hr/learning/sessions',
    session: (id) => `/hr/learning/sessions/${id}`,
    participation: (id) => `/hr/learning/sessions/${id}/participation`,
    programs: '/hr/learning/programs',
    program: (id) => `/hr/learning/programs/${id}`,
    programModules: (id) => `/hr/learning/programs/${id}/modules`,
    enroll: (id) => `/hr/learning/programs/${id}/enroll`,
    completeModule: (enrollmentId, moduleId) =>
      `/hr/learning/enrollments/${enrollmentId}/modules/${moduleId}/complete`,
    questions: '/hr/learning/questions',
    assessments: '/hr/learning/assessments',
    assessment: (id) => `/hr/learning/assessments/${id}`,
    startAttempt: (id) => `/hr/learning/assessments/${id}/start`,
    submitAttempt: (id) => `/hr/learning/attempts/${id}/submit`,
    evaluateAttempt: (id) => `/hr/learning/attempts/${id}/evaluate`,
    jobs: '/hr/recruitment/jobs',
    job: (id) => `/hr/recruitment/jobs/${id}`,
    candidates: '/hr/recruitment/candidates',
    candidate: (id) => `/hr/recruitment/candidates/${id}`,
    candidateOutcome: (id) => `/hr/recruitment/candidates/${id}/outcome`,
    interviews: '/hr/recruitment/interviews',
    interviewAssessments: (id) => `/hr/recruitment/interviews/${id}/assessments`,
    settings: '/hr/settings',
  },
};

// Helper function to build full URL
export const buildApiUrl = (endpoint) => {
  return `${API_BASE_URL}${endpoint}`;
};